import useAuth from '@/auth/application/useAuth';
import { FEATURE_FLAGS } from '@/common/domain/constants/featureFlags';
import { useFeatureFlag } from '@/common/presentation/hooks/useFeatureFlag';
import { isCrmLicenseRevokedFor, useCrmLicenseRevokedToken } from '@/crm/application/crmLicenseRevocation';

export interface CanAccessCrmResult {
  /** Whether CRM should be visible/reachable for the current user. */
  allowed: boolean;
  /**
   * Whether the rule has finished settling (auth initialized and the LaunchDarkly
   * identify() attempt for the current identity has either succeeded or failed).
   * While false, `allowed` is not yet meaningful — callers should render a
   * loading/neutral state rather than a final allow/deny decision.
   */
  isReady: boolean;
}

/**
 * The single canonical CRM availability rule (LLAMS-465):
 *
 *   CanAccessCrm = ldIdentityStatus === 'succeeded'
 *                  AND ldFlagEnabled(CRM_MODULE_ENABLED)
 *                  AND user.crmLicense === true
 *                  AND NOT revoked mid-session for the current token (crm:unlicensed)
 *
 * `ldIdentityStatus === 'succeeded'` is required — not just "settled" — so a failed
 * LaunchDarkly identify() (context never actually updated to the real organization)
 * can never let a stale/default flag evaluation enable CRM. `CrmAdmin` deliberately
 * never participates here: it is an administrative privilege inside CRM, not an
 * entitlement to access it.
 *
 * This is the ONLY place this combination is computed — reused by the sidebar nav
 * item and the /crm/* route guard so the rule can never drift between them.
 */
export function useCanAccessCrm(): CanAccessCrmResult {
  const { user, isInitialized, ldIdentityStatus, getToken } = useAuth();
  const crmFlagEnabled = useFeatureFlag(FEATURE_FLAGS.CRM_MODULE_ENABLED);
  // LLAMS-465: the remote reported `403 crm.license_required` for the token we currently hold.
  const revokedToken = useCrmLicenseRevokedToken();
  const licenseRevoked = isCrmLicenseRevokedFor(revokedToken, getToken?.());

  // Explicitly enumerate the settled states rather than negating 'pending' — this
  // also fails safe (isReady=false) for an undefined status, which only occurs in
  // hand-built test doubles that omit the field, never in real AuthContext state.
  const isReady = Boolean(isInitialized) && (ldIdentityStatus === 'succeeded' || ldIdentityStatus === 'failed');
  const identityTrusted = ldIdentityStatus === 'succeeded';
  const allowed = isReady && identityTrusted && crmFlagEnabled === true && user?.crmLicense === true && !licenseRevoked;

  return { allowed, isReady };
}
